import React, { useState, useEffect } from 'react';
import { apiWithAuth } from '../../lib/api';
import toast from 'react-hot-toast';
import {
    SparklesIcon,
    CheckCircleIcon,
    CreditCardIcon,
    MegaphoneIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const EmployerPlanPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState({
        scout_expires_at: null,
        scout_price: 5000,
        promoted_until: null,
        priority_price: 3000
    });

    useEffect(() => {
        fetchSubscriptionStatus();
    }, []);

    const fetchSubscriptionStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await apiWithAuth(token).get('/employer/subscription/status');
            if (response.status === 200) {
                setSubscription(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch subscription status:', error);
            toast.error('プラン情報の取得に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const isScoutActive = () => {
        if (!subscription.scout_expires_at) return false;
        return new Date(subscription.scout_expires_at) > new Date();
    };

    const isPriorityActive = () => {
        if (!subscription.promoted_until) return false;
        return new Date(subscription.promoted_until) > new Date();
    };

    const handlePurchaseScout = async () => {
        try {
            if (!window.confirm(`スカウト機能（月額${subscription.scout_price.toLocaleString()}円）を購入しますか？\n登録済みのクレジットカードで決済されます。`)) {
                return;
            }

            setLoading(true);
            const token = localStorage.getItem('token');
            const idempotencyKey = `sub_scout_${Date.now()}`;

            const response = await apiWithAuth(token).post('/employer/subscription/scout', {
                idempotency_key: idempotencyKey
            });

            if (response.status === 200) {
                toast.success('購入が完了しました！');
                fetchSubscriptionStatus();
            }
        } catch (error) {
            console.error('Purchase failed:', error);
            if (error.response?.status === 402) {
                toast.error('決済に失敗しました。カード情報を確認してください。');
                navigate('/employer/credit-card');
            } else if (error.response?.status === 400) {
                toast.error('クレジットカードが登録されていません');
                navigate('/employer/credit-card');
            } else {
                toast.error('購入処理に失敗しました');
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePurchasePriority = async () => {
        try {
            const price = subscription.priority_price || 3000;
            if (!window.confirm(`優先表示機能（月額${price.toLocaleString()}円）を購入しますか？\n登録済みのクレジットカードで決済されます。`)) {
                return;
            }

            setLoading(true);
            const token = localStorage.getItem('token');
            const idempotencyKey = `sub_priority_${Date.now()}`;

            const response = await apiWithAuth(token).post('/employer/subscription/priority', {
                idempotency_key: idempotencyKey
            });

            if (response.status === 200) {
                toast.success('購入が完了しました！');
                fetchSubscriptionStatus();
            }
        } catch (error) {
            console.error('Purchase failed:', error);
            if (error.response?.status === 402) {
                toast.error('決済に失敗しました。カード情報を確認してください。');
                navigate('/employer/credit-card');
            } else if (error.response?.status === 400) {
                toast.error('クレジットカードが登録されていません');
                navigate('/employer/credit-card');
            } else {
                toast.error('購入処理に失敗しました');
            }
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="p-8 text-center">読み込み中...</div>;
    }

    return (
        <div className="max-w-6xl mx-auto px-4 py-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-8">有料プラン / サブスクリプション</h1>

            <div className="grid md:grid-cols-2 gap-8">

                {/* Scout Plan Card */}
                <div className={`bg-white rounded-xl shadow-lg border-2 overflow-hidden ${isScoutActive() ? 'border-green-500' : 'border-gray-200'}`}>
                    <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                            <div className="p-3 bg-yellow-100 rounded-lg">
                                <SparklesIcon className="w-8 h-8 text-yellow-600" />
                            </div>
                            {isScoutActive() ? (
                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                                    有効
                                </span>
                            ) : (
                                <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm font-medium">
                                    未加入
                                </span>
                            )}
                        </div>

                        <h2 className="text-xl font-bold text-gray-900 mb-2">スカウト機能プラン</h2>
                        <p className="text-gray-500 mb-6 min-h-[48px]">
                            求職者に直接メッセージ（スカウト）を送信できる機能です。
                            積極的な採用活動を行いたい場合に推奨されます。
                        </p>

                        <div className="mb-6">
                            <span className="text-3xl font-bold text-gray-900">
                                ¥{(subscription.scout_price || 5000).toLocaleString()}
                            </span>
                            <span className="text-gray-500"> / 月</span>
                        </div>

                        <div className="space-y-3 mb-8">
                            <div className="flex items-center text-sm text-gray-600">
                                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                                <span>求職者へのダイレクトメッセージ送信</span>
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                                <span>スカウトテンプレートの利用</span>
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                                <span>既読ステータスの確認</span>
                            </div>
                        </div>

                        {isScoutActive() ? (
                            <div className="bg-green-50 rounded-lg p-4 mb-4">
                                <p className="text-sm text-green-800 font-medium mb-1">現在のプラン有効期限</p>
                                <p className="text-lg text-green-900 font-bold">
                                    {new Date(subscription.scout_expires_at).toLocaleDateString('ja-JP')} まで
                                </p>
                            </div>
                        ) : null}

                        <button
                            onClick={handlePurchaseScout}
                            className={`w-full py-3 rounded-lg font-bold text-white transition-colors flex items-center justify-center ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                            disabled={loading}
                        >
                            {loading ? (
                                '処理中...'
                            ) : (
                                <>
                                    <CreditCardIcon className="w-5 h-5 mr-2" />
                                    {isScoutActive() ? '期間を延長する' : '購入する'}
                                </>
                            )}
                        </button>
                        <p className="text-xs text-gray-400 text-center mt-3">
                            ※登録済みのクレジットカードで決済されます
                        </p>
                    </div>
                </div>

                {/* Priority Listing Plan Card */}
                <div className={`bg-white rounded-xl shadow-lg border-2 overflow-hidden ${isPriorityActive() ? 'border-green-500' : 'border-gray-200'}`}>
                    <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                            <div className="p-3 bg-indigo-100 rounded-lg">
                                <MegaphoneIcon className="w-8 h-8 text-indigo-600" />
                            </div>
                            {isPriorityActive() ? (
                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                                    有効
                                </span>
                            ) : (
                                <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm font-medium">
                                    未加入
                                </span>
                            )}
                        </div>

                        <h2 className="text-xl font-bold text-gray-900 mb-2">優先表示オプション</h2>
                        <p className="text-gray-500 mb-6 min-h-[48px]">
                            求人検索結果で、あなたの求人を他の求人よりも優先的に上位表示します。
                            露出を増やし、応募数を最大化したい場合に最適です。
                        </p>

                        <div className="mb-6">
                            <span className="text-3xl font-bold text-gray-900">
                                ¥{(subscription.priority_price || 3000).toLocaleString()}
                            </span>
                            <span className="text-gray-500"> / 月</span>
                        </div>

                        <div className="space-y-3 mb-8">
                            <div className="flex items-center text-sm text-gray-600">
                                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                                <span>求人検索結果での最上位表示</span>
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                                <span>注目度アップによる応募数増加</span>
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                                <span>「注目」バッジの表示</span>
                            </div>
                        </div>

                        {isPriorityActive() ? (
                            <div className="bg-green-50 rounded-lg p-4 mb-4">
                                <p className="text-sm text-green-800 font-medium mb-1">現在のプラン有効期限</p>
                                <p className="text-lg text-green-900 font-bold">
                                    {new Date(subscription.promoted_until).toLocaleDateString('ja-JP')} まで
                                </p>
                            </div>
                        ) : null}

                        <button
                            onClick={handlePurchasePriority}
                            className={`w-full py-3 rounded-lg font-bold text-white transition-colors flex items-center justify-center ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                                }`}
                            disabled={loading}
                        >
                            {loading ? (
                                '処理中...'
                            ) : (
                                <>
                                    <CreditCardIcon className="w-5 h-5 mr-2" />
                                    {isPriorityActive() ? '期間を延長する' : '購入する'}
                                </>
                            )}
                        </button>
                        <p className="text-xs text-gray-400 text-center mt-3">
                            ※登録済みのクレジットカードで決済されます
                        </p>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default EmployerPlanPage;
